import { isPromise } from './isPromise'

type Updater<T = void> = (update: T) => Awaitable<void>
export type Subscribable<T = void> = {
	/**
	 * Invoke updater-function on updates
	 *
	 * @returns Unsubscribe function
	 */
	subscribe: (updater: Updater<T>) => () => void
}

/**
 * Helpers for creating subscribables
 */
export type SubscribableInternal<T = void> = {
	/**
	 * Calls all subscribed functions. If any subscribed function returns a promise, this will return a promise awaiting
	 * all subscriptions.
	 */
	update: (update: T, options?: { continueUpdate?: () => boolean }) => Awaitable<void>
	subscribe: (updater: Updater<T>) => () => void
}

type SubscribableOptions = {
	/**
	 * Order updaters are invoked in. This is most important when using the
	 * `continueUpdate` hook to abort an update halfway through. With
	 * `oldestFirst` (default), the first and most fundamental updaters are run
	 * first. With `newestFirst`, new updaters can be added, suppress an update
	 * for other updaters and then be removed again.
	 */
	updateOrder?: 'oldestFirst' | 'newestFirst'
}

/**
 * Create internal logic for Subscribables
 */
export const subscribableCreate = <T = void>({ updateOrder }: SubscribableOptions = {}): SubscribableInternal<T> => {
	let updaters: Array<Updater<T>> = []
	const oldestFirst = updateOrder !== 'newestFirst'

	return {
		update: (update, options) => {
			const promises = updaters
				.map((updater) => {
					if (options?.continueUpdate?.() ?? true) {
						return updater(update)
					}
					return undefined
				})
				.filter(isPromise<void>)

			return promises.length > 0 ? Promise.all(promises).then(() => undefined) : undefined
		},
		subscribe: (updater) => {
			updaters = oldestFirst ? [...updaters, updater] : [updater, ...updaters]

			return () => {
				updaters = updaters.filter((u) => u !== updater)
			}
		},
	}
}
